import { yupResolver } from '@hookform/resolvers/yup';
import {
  FILTER_RESET_VALUES,
  ID_FILTER_RESET_VALUES,
} from 'pages/Orders/constants/filterResetValues';
import { OrdersListFilterFormValues } from 'pages/Orders/OrdersList/OrdersListContent';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext } from 'react-admin';
import { getAppliedTimezone } from 'shared/mui/DatePicker/utils/getAppliedTimezone';
import { useFilters, UseFiltersReturnValues } from 'shared/mui/Filter/hooks/useFilters';
import { useIdFilters, UseIdFiltersReturnValues } from 'shared/mui/Filter/hooks/useIdFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';
import * as yup from 'yup';

type UseOrdersFiltersResult = {
  filtersForm: UseFiltersReturnValues<OrdersListFilterFormValues>;
  idFiltersForm: UseIdFiltersReturnValues;
};

export function useOrdersFilters(): UseOrdersFiltersResult {
  const translate = useTypedTranslate();
  const { filterValues } = useListContext();

  const filtersForm = useFilters<OrdersListFilterFormValues>({
    mode: 'all',
    resolver: yupResolver<any>(
      yup.object().shape({
        amountPenny: yup
          .object()
          .shape({
            gte: yup
              .number()
              .typeError(translate('app.validation.number.isNumber'))
              .positive(translate('app.validation.number.noNegative'))
              .when('lte', {
                is: (val: number | undefined) => Boolean(val),
                then: () =>
                  yup
                    .number()
                    .typeError(translate('app.validation.number.isNumber'))
                    .nullable()
                    .lessThan(
                      yup.ref('lte'),
                      translate('app.validation.number.lessThan', {
                        max: translate('app.filter.amountTo'),
                      }),
                    ),
              })
              .integer(translate('app.validation.number.integer'))
              .nullable(),
            lte: yup
              .number()
              .typeError(translate('app.validation.number.isNumber'))
              .positive(translate('app.validation.number.noNegative'))
              .moreThan(
                yup.ref('gte'),
                translate('app.validation.number.greaterThan', {
                  min: translate('app.filter.amountFrom'),
                }),
              )
              .integer(translate('app.validation.number.integer'))
              .nullable(),
          })
          .nullable(),
      }),
    ),
    defaultValues: {
      merchant: filterValues?.merchant || null,
      appliedDate: [
        formatInputToTimestamp(filterValues?.createdAt?.gte),
        formatInputToTimestamp(filterValues?.createdAt?.lte),
      ],
      timezone: filterValues.timezone || null,
      amountPenny: {
        lte: filterValues?.amountPenny?.lte || null,
        gte: filterValues?.amountPenny?.gte || null,
      },
      currency: filterValues?.currency || [],
      direction: filterValues?.direction || null,
      gateway: filterValues?.gateway || [],
      provider: filterValues?.['gateway.provider'] || null,
      method: filterValues?.method || [],
      status: filterValues?.status || [],
      customer: filterValues?.customer || null,
      orderProviderExternalError: {
        providerExternalError: {
          reason:
            filterValues?.orderExternalErrorReason ||
            filterValues?.orderProviderExternalError?.providerExternalError?.reason ||
            null,
        },
      },
      matchRejectReason: filterValues?.matchRejectReason
        ? Boolean(Number(filterValues?.matchRejectReason))
        : null,
    },
    resetValues: FILTER_RESET_VALUES,
  });

  const idFiltersForm = useIdFilters({
    defaultValues: {
      merchantReference: filterValues?.merchantReference || [],
      id: filterValues?.id || [],
      providerReferenceId: filterValues?.providerReferenceId || [],
      anyId: filterValues?.anyId || [],
    },
    resetValues: ID_FILTER_RESET_VALUES,
  });

  useSyncFilter<OrdersListFilterFormValues>({
    appliedFilters: filtersForm.appliedFilters,
    appliedIdFilters: idFiltersForm.appliedIdFilters,
    transform: (appliedFilters) => {
      const {
        appliedDate = [null, null],
        timezone,
        provider,
        orderProviderExternalError,
        matchRejectReason,
        ...restFilters
      } = appliedFilters;
      return cleanEmpty({
        ...restFilters,
        'gateway.provider': provider,
        createdAt: {
          gte: formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
          lte: formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        },
        timezone: getAppliedTimezone(timezone, appliedDate),
        orderProviderExternalError: !matchRejectReason ? orderProviderExternalError : undefined,
        orderExternalErrorReason: matchRejectReason
          ? orderProviderExternalError?.providerExternalError?.reason
          : null,
        matchRejectReason:
          matchRejectReason === true ? '1' : matchRejectReason === false ? '0' : matchRejectReason,
      });
    },
  });

  return { filtersForm, idFiltersForm };
}
